import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BusinessRulesAcceptancePublicData } from '@/types/customerSupport';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function BusinessRulesAcceptancePage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<BusinessRulesAcceptancePublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/public/acceptance/${token}`)
      .then(res => {
        if (!res.ok) throw new Error('Não encontrado');
        return res.json();
      })
      .then(result => {
        setData(result);
        if (result.acceptance?.accepted_at) {
          setAccepted(true);
        }
      })
      .catch(() => setError('Link inválido ou expirado.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const res = await fetch(`${API_BASE}/public/acceptance/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Erro ao registrar aceite');
      setAccepted(true);
    } catch {
      alert('Erro ao registrar aceite. Tente novamente.');
    } finally {
      setAccepting(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!token) return;
    window.open(`${API_BASE}/public/acceptance/${token}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'Arial, sans-serif' }}>
        <p>Carregando...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '8px' }}>Link inválido</h2>
          <p style={{ color: '#666' }}>Este link de aceite não foi encontrado ou não é mais válido.</p>
        </div>
      </div>
    );
  }

  const interviewDate = data.interview_date
    ? new Date(data.interview_date + 'T12:00:00').toLocaleDateString('pt-BR')
    : '-';

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 16px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', borderBottom: '2px solid #22c55e', paddingBottom: '16px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', margin: '0' }}>RESUMO DA REGRA DE NEGÓCIO</h1>
        <p style={{ color: '#666', margin: '4px 0 0', fontSize: '14px' }}>Aceite Digital</p>
      </div>

      {/* Client data */}
      {data.client && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', color: '#22c55e', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>DADOS DO CLIENTE</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '6px 8px', fontWeight: 'bold', width: '160px', color: '#374151' }}>Razão Social:</td>
                <td style={{ padding: '6px 8px' }}>{data.client.legal_name}</td>
              </tr>
              <tr style={{ background: '#f9fafb' }}>
                <td style={{ padding: '6px 8px', fontWeight: 'bold', color: '#374151' }}>CNPJ:</td>
                <td style={{ padding: '6px 8px' }}>{data.client.company_tax_id}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 8px', fontWeight: 'bold', color: '#374151' }}>Email:</td>
                <td style={{ padding: '6px 8px' }}>{data.client.email || '-'}</td>
              </tr>
              <tr style={{ background: '#f9fafb' }}>
                <td style={{ padding: '6px 8px', fontWeight: 'bold', color: '#374151' }}>Telefone:</td>
                <td style={{ padding: '6px 8px' }}>{data.client.phone_number || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Interview date */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', color: '#22c55e', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>ENTREVISTA DE REGRAS DE NEGÓCIO</h3>
        <p style={{ margin: '8px 0' }}><strong>Data da entrevista:</strong> {interviewDate}</p>
      </div>

      {/* Products + Rules */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', color: '#22c55e', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>PRODUTOS E REGRAS DE NEGÓCIO</h3>
        {data.rules.map((rule, idx) => (
          <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', margin: '12px 0' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '14px' }}>{rule.product_name}</h4>
            <p style={{ margin: 0, color: '#374151' }}>{rule.rule_description || 'Não informado'}</p>
          </div>
        ))}
      </div>

      {/* Acceptance */}
      {accepted ? (
        <div style={{
          border: '2px solid #22c55e', borderRadius: '8px', padding: '20px',
          textAlign: 'center', background: '#f0fdf4',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 'bold', color: '#22c55e' }}>
            Aceite Registrado
          </p>
          <p style={{ margin: '0 0 12px', color: '#374151', fontSize: '14px' }}>
            {data.acceptance.accepted_at
              ? `Aceito em ${new Date(data.acceptance.accepted_at).toLocaleString('pt-BR')}`
              : 'Aceite registrado com sucesso!'}
          </p>
          <button
            onClick={handleDownloadPdf}
            style={{
              background: '#22c55e', color: 'white', padding: '10px 24px',
              border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer',
            }}
          >
            Baixar PDF
          </button>
        </div>
      ) : (
        <div style={{
          border: '2px solid #22c55e', borderRadius: '8px', padding: '20px',
          textAlign: 'center', background: '#f0fdf4',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 'bold' }}>ACEITE DIGITAL</p>
          <p style={{ margin: '0 0 12px', color: '#374151', fontSize: '14px' }}>
            Ao clicar no botão abaixo, declaro que estou de acordo com as regras de negócio descritas neste documento.
          </p>
          <button
            onClick={handleAccept}
            disabled={accepting}
            style={{
              background: accepting ? '#86efac' : '#22c55e', color: 'white',
              padding: '12px 32px', border: 'none', borderRadius: '6px',
              fontSize: '15px', fontWeight: 'bold', cursor: accepting ? 'not-allowed' : 'pointer',
            }}
          >
            {accepting ? 'Registrando...' : 'Estou de Acordo'}
          </button>
          <p style={{ margin: '12px 0 0', fontSize: '11px', color: '#9ca3af' }}>
            O aceite será registrado com data, hora e IP do dispositivo.
          </p>
        </div>
      )}
    </div>
  );
}
